import { combineStateReducer } from "../utils/combineStateReducer";
import { State, Selector } from "./types";

import * as createEvent from "./createEvent";
import * as createList from "./createList";
import * as createMilestone from "./createMilestone";
import * as createTask from "./createTask";
import * as createKanbanTask from "./createKanbanTask";
import * as createTaskStatus from "./createTaskStatus";
import * as editEvent from "./editEvent";
import * as editList from "./editList";
import * as editMilestone from "./editMilestone";
import * as editTask from "./editTask";
import * as editTaskStatus from "./editTaskStatus";
import * as markMilestoneAsCompleted from "./markMilestoneAsCompleted";
import * as markTaskAsCompleted from "./markTaskAsCompleted";
import * as moveKanbanTask from "./moveKanbanTask";
import * as moveListElement from "./moveListElement";
import * as removeEvent from "./removeEvent";
import * as removeList from "./removeList";
import * as removeMilestone from "./removeMilestone";
import * as removeTask from "./removeTask";
import * as removeTaskStatus from "./removeTaskStatus";
import * as retrieveEvent from "./retrieveEvent";
import * as retrieveKanban from "./retrieveKanban";
import * as retrieveList from "./retrieveList";
import * as retrieveLists from "./retrieveLists";
import * as retrieveMilestone from "./retrieveMilestone";
import * as retrieveProjectGantt from "./retrieveProjectGantt";
import * as retrieveTask from "./retrieveTask";
import * as retrieveTasks from "./retrieveTasks";
import * as retrieveTaskStatuses from "./retrieveTaskStatuses";
import * as duplicateList from "./duplicateList";
import * as retrieveProjectLogs from "./retrieveProjectLogs";
import * as retrieveWbsMeta from "./retrieveWbsMeta";
const initialState: State = {
  createEvent: createEvent.initialState,
  createList: createList.initialState,
  createMilestone: createMilestone.initialState,
  createTask: createTask.initialState,
  createKanbanTask: createKanbanTask.initialState,
  createTaskStatus: createTaskStatus.initialState,
  editEvent: editEvent.initialState,
  editList: editList.initialState,
  editMilestone: editMilestone.initialState,
  editTask: editTask.initialState,
  editTaskStatus: editTaskStatus.initialState,
  markMilestoneAsCompleted: markMilestoneAsCompleted.initialState,
  markTaskAsCompleted: markTaskAsCompleted.initialState,
  moveKanbanTask: moveKanbanTask.initialState,
  moveListElement: moveListElement.initialState,
  removeEvent: removeEvent.initialState,
  removeList: removeList.initialState,
  removeMilestone: removeMilestone.initialState,
  removeTask: removeTask.initialState,
  removeTaskStatus: removeTaskStatus.initialState,
  retrieveEvent: retrieveEvent.initialState,
  retrieveKanban: retrieveKanban.initialState,
  retrieveList: retrieveList.initialState,
  retrieveLists: retrieveLists.initialState,
  retrieveMilestone: retrieveMilestone.initialState,
  retrieveProjectGantt: retrieveProjectGantt.initialState,
  retrieveTask: retrieveTask.initialState,
  retrieveTasks: retrieveTasks.initialState,
  retrieveTaskStatuses: retrieveTaskStatuses.initialState,
  duplicateList: duplicateList.initialState,
  retrieveProjectLogs: retrieveProjectLogs.initialState,
  retrieveWbsMeta: retrieveWbsMeta.initialState,
};

const reduceState = combineStateReducer(initialState, {
  createEvent: createEvent.reduceState,
  createList: createList.reduceState,
  createMilestone: createMilestone.reduceState,
  createTask: createTask.reduceState,
  createKanbanTask: createKanbanTask.reduceState,
  createTaskStatus: createTaskStatus.reduceState,
  editEvent: editEvent.reduceState,
  editList: editList.reduceState,
  editMilestone: editMilestone.reduceState,
  editTask: editTask.reduceState,
  editTaskStatus: editTaskStatus.reduceState,
  markMilestoneAsCompleted: markMilestoneAsCompleted.reduceState,
  markTaskAsCompleted: markTaskAsCompleted.reduceState,
  moveKanbanTask: moveKanbanTask.reduceState,
  moveListElement: moveListElement.reduceState,
  removeEvent: removeEvent.reduceState,
  removeList: removeList.reduceState,
  removeMilestone: removeMilestone.reduceState,
  removeTask: removeTask.reduceState,
  removeTaskStatus: removeTaskStatus.reduceState,
  retrieveEvent: retrieveEvent.reduceState,
  retrieveKanban: retrieveKanban.reduceState,
  retrieveList: retrieveList.reduceState,
  retrieveLists: retrieveLists.reduceState,
  retrieveMilestone: retrieveMilestone.reduceState,
  retrieveProjectGantt: retrieveProjectGantt.reduceState,
  retrieveTask: retrieveTask.reduceState,
  retrieveTasks: retrieveTasks.reduceState,
  retrieveTaskStatuses: retrieveTaskStatuses.reduceState,
  duplicateList: duplicateList.reduceState,
  retrieveProjectLogs: retrieveProjectLogs.reduceState,
  retrieveWbsMeta: retrieveWbsMeta.reduceState,
});

function createSelector(getState: () => State): Selector {
  return {
    ...createEvent.createSelector(() => getState().createEvent),
    ...createList.createSelector(() => getState().createList),
    ...createMilestone.createSelector(() => getState().createMilestone),
    ...createTask.createSelector(() => getState().createTask),
    ...createKanbanTask.createSelector(() => getState().createKanbanTask),
    ...createTaskStatus.createSelector(() => getState().createTaskStatus),
    ...editEvent.createSelector(() => getState().editEvent),
    ...editList.createSelector(() => getState().editList),
    ...editMilestone.createSelector(() => getState().editMilestone),
    ...editTask.createSelector(() => getState().editTask),
    ...editTaskStatus.createSelector(() => getState().editTaskStatus),
    ...markMilestoneAsCompleted.createSelector(
      () => getState().markMilestoneAsCompleted,
    ),
    ...markTaskAsCompleted.createSelector(() => getState().markTaskAsCompleted),
    ...moveKanbanTask.createSelector(() => getState().moveKanbanTask),
    ...moveListElement.createSelector(() => getState().moveListElement),
    ...removeEvent.createSelector(() => getState().removeEvent),
    ...removeList.createSelector(() => getState().removeList),
    ...removeMilestone.createSelector(() => getState().removeMilestone),
    ...removeTask.createSelector(() => getState().removeTask),
    ...removeTaskStatus.createSelector(() => getState().removeTaskStatus),
    ...retrieveEvent.createSelector(() => getState().retrieveEvent),
    ...retrieveKanban.createSelector(() => getState().retrieveKanban),
    ...retrieveList.createSelector(() => getState().retrieveList),
    ...retrieveLists.createSelector(() => getState().retrieveLists),
    ...retrieveMilestone.createSelector(() => getState().retrieveMilestone),
    ...retrieveProjectGantt.createSelector(
      () => getState().retrieveProjectGantt,
    ),
    ...retrieveTask.createSelector(() => getState().retrieveTask),
    ...retrieveTasks.createSelector(() => getState().retrieveTasks),
    ...retrieveTaskStatuses.createSelector(
      () => getState().retrieveTaskStatuses,
    ),
    ...duplicateList.createSelector(() => getState().duplicateList),
    ...retrieveProjectLogs.createSelector(() => getState().retrieveProjectLogs),
    ...retrieveWbsMeta.createSelector(() => getState().retrieveWbsMeta),
  };
}

export type { State, Selector };

export { reduceState, initialState, createSelector };
